import { Suspense, useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import * as Form from 'components/form';
import { TaskForm } from 'components/tasks/form/TaskForm';
import { taskFormToRequest } from 'components/tasks/form/upstream';
import { TaskFooter } from 'components/tasks/single/TaskFooter';
import { TaskHeader } from 'components/tasks/single/TaskHeader';
import { TaskHistory } from 'components/tasks/single/TaskHistory';
import { useTaskDrawerState } from 'components/tasks/useTaskDrawerState';
import { hasLinkSearchNodes } from 'components/tasks/utils/editor-search-helpers';
import { useTaskForm } from 'components/tasks/utils/useTaskForm';
import { useUpdateTask } from 'components/tasks/utils/useUpdateTask';
import useDebounce from 'customHooks/useDebounce';

const AUTOSAVE_DEBOUNCE = 1000;

const SingleTask = () => {
    const { selectedTaskId } = useTaskDrawerState();
    const hasEdited = useRef(false);
    const { methods } = useTaskForm({ taskId: selectedTaskId });
    const {
        control,
        getValues,
        formState: { isDirty },
    } = methods;

    const { mutate } = useUpdateTask();

    const watchedValues = useWatch({ control });
    const values = useMemo(
        () => ({ ...watchedValues, ...getValues() }),
        [watchedValues, getValues],
    );

    const debouncedValues = useDebounce(values, AUTOSAVE_DEBOUNCE);
    useEffect(() => {
        if (
            hasEdited.current &&
            selectedTaskId &&
            !hasLinkSearchNodes(debouncedValues.description)
        ) {
            mutate({ taskId: selectedTaskId, params: taskFormToRequest(debouncedValues) });
        }
    }, [debouncedValues, selectedTaskId, mutate]);

    useEffect(() => {
        if (isDirty) {
            hasEdited.current = true;
        }
    }, [isDirty]);

    if (!selectedTaskId) return null;

    return (
        <Form.Provider {...methods}>
            <form className="flex h-full flex-col" onSubmit={e => e.preventDefault()}>
                <TaskHeader taskId={selectedTaskId} />
                <div className="flex flex-grow flex-col overflow-y-auto">
                    <div className="px-8">
                        <div className="border-b border-gray-200 pb-6">
                            <TaskForm />
                        </div>
                    </div>
                    <Suspense fallback={null}>
                        <TaskHistory taskId={selectedTaskId} />
                    </Suspense>
                </div>

                <TaskFooter />
            </form>
        </Form.Provider>
    );
};

export { SingleTask };
